import { Injectable } from '@angular/core';
import { PeerComparisonAnalysis, PeerComparisonAnalysisService, PeerComparisonGroup, PeerComparisonGroupBuilder, PeerComparisonGroupService, PeerGroups } from '@ddt-web/compare-against-peers';
import { StoreService } from '@ddt-web/store-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as cloneDeep from 'lodash/cloneDeep';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, first, map } from 'rxjs/operators';

import { ErrorData } from '../models';

@UntilDestroy()
@Injectable({
	providedIn: 'root'
})
export class CompareAgainstPeersDataService {
	private peerGroups: PeerComparisonGroup[];

	/**
	 * This service only deal with fetch and store data from /wds/saved-portfolios/analysis
	 */
	constructor(
		private peerComparisonGroupService: PeerComparisonGroupService,
		private storeService: StoreService,
		private peerComparisonAnalysisService: PeerComparisonAnalysisService
	) {
		this.peerComparisonGroupService
			.getPeerComparisonGroups()
			.pipe(first())
			.subscribe(groups => this.peerGroups = groups);
	}

	public fetchPeerComparisonForPortfolio(details, positionDetails, configs): void {
		combineLatest([
			this.storeService.selectAll(details),
			this.storeService.selectAll(positionDetails),
			this.peerComparisonAnalysisService.getPeerComparisonGroupData().pipe(
				catchError(err => {
					console.error(`Peer comparison data call failed to execute succesfully`, err);
					return of([]);
				})
			)
		]).pipe(
			filter(([data, positionDetails, peerGroups]) =>
				positionDetails && !positionDetails.isLoading && peerGroups && data && !data.isLoading
			),
			map(([data, positionDetails, peerGroups]) => {
				const peersGroupResponse = this.peerComparisonAnalysisService.getPeerComparisonGroupAnalysisFromResponse(peerGroups, positionDetails);
				return [data, peersGroupResponse];
			}),
			untilDestroyed(this))
			.subscribe(([data, peerGroups]: [any, PeerComparisonAnalysis[]]) => {
				if (data?.entities) {
					const peers: any[] = [];
					Object.keys(data.entities).forEach(id => {
						// get equities of each portfolio and map to peer group
						const portfolio = data.entities[id] as any;
						if (portfolio?.portfolioBreakdowns && peerGroups?.length) {
							const assetAllocationSectors = portfolio.portfolioBreakdowns.find(breakdowns => breakdowns.name === 'assetAllocation')?.sectors ?? [];
							const usOrNonUsStocksData = assetAllocationSectors.filter(assets => assets.name === 'usStock' || assets.name === 'nonUsStock') ?? [];
							const equity = usOrNonUsStocksData.reduce((sum, asset) => sum + asset?.contribution, 0);

							const peerComparisonGroup = this.getPeerComparisonGroupByEquity(equity);
							const peerData = cloneDeep(peerGroups.find(peer => peer.peerComparisonGroup === PeerGroups[peerComparisonGroup.groupName])) ?? [];
							peerData['id'] = portfolio.id;
							peerData['pcGroup'] = new PeerComparisonGroupBuilder().setByGroupName(peerComparisonGroup.groupName).build();
							peers.push(peerData);
						} else {
							peers.push(this.createErrorObject(portfolio.id));
						}
					});
					if (peers.length) {
						this.updateStoreFromResponse(configs, peers);
					} else {
						peers.push(this.createErrorObject());
						this.updateStoreFromResponse(configs, peers);
					}
				}
			});
	}

	private getPeerComparisonGroupByEquity(portEquity: number): PeerComparisonGroup {
		let peerGroup: PeerComparisonGroup = this.peerGroups.find(group => group.minRange <= portEquity * 100 && portEquity * 100 <= group.maxRange);

		if (!peerGroup) {
			const sortedGroups = [...this.peerGroups].sort((a, b) => a.minRange - b.minRange);
			if (portEquity * 100 < sortedGroups[0].minRange) {
				peerGroup = sortedGroups[0];
			} else {
				// assume most aggressive peer group
				peerGroup = sortedGroups[sortedGroups.length - 1];
			}
			console.warn(`Portfolio equity allocation out of bounds ${portEquity} using ${peerGroup.groupName} peer group`);
		}
		return peerGroup;
	}

	/**
	 * Add or Update store with response
	 */
	private updateStoreFromResponse(configs: any, response: any): void {
		if (response) {
			configs.forEach(config => this.storeService.addMany(config, response));
		}
	}

	private createErrorObject(portfolioId?: string): any {
		const errorObject: ErrorData = {
			id: portfolioId,
			success: false,
			allDataReturned: false,
			errorCodes: ['CAP_DATA_RETRIEVAL_FAILED'],
			failureReason: 'Error getting CAP data',
			debugMessage: 'Portfolio retrieval from details store failed.',
			hideOnError: false
		};
		return errorObject;
	}
}
